const AUTO_ROTATE_MS = 5200;
const SWIPE_ACTIVATE_PX = 10;
const SWIPE_COMMIT_PX = 48;

/**
 * @typedef {Object} UpsellItem
 * @property {string} id
 * @property {'whisk' | 'shaker' | 'flavor' | 'soda'} type
 * @property {string} handle
 * @property {number} variantId
 * @property {string} title
 * @property {string} subtitle
 * @property {string} price
 * @property {string} image
 */

/**
 * @typedef {Object} UpsellConfig
 * @property {{ maxTotal: number, maxWhisk: number, maxShaker: number, maxFlavor: number, maxSoda: number }} limits
 * @property {{ heading: string, add: string, showOption: string }} labels
 * @property {UpsellItem[]} items
 */

/**
 * Cart.js line items do not include product handle; parse from line URL.
 * @param {{ handle?: string, url?: string, sku?: string }} line
 * @returns {string}
 */
function lineHandle(line) {
  if (line?.handle) {
    return String(line.handle).toLowerCase();
  }

  const url = line?.url || '';
  const match = url.match(/\/products\/([^/?#]+)/i);
  return match ? match[1].toLowerCase() : '';
}

/**
 * @returns {Promise<{ items?: Array<{ handle?: string, variant_id?: number, sku?: string, url?: string }>, item_count?: number } | null>}
 */
async function fetchCartJson() {
  try {
    const url = new URL('/cart.js', window.location.origin);
    url.searchParams.set('_', String(Date.now()));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
}

class ShinzoCartDrawerUpsells extends HTMLElement {
  /** @type {UpsellConfig | null} */
  #config = null;

  /** @type {number} */
  #activeIndex = 0;

  /** @type {number} */
  #slideCount = 0;

  /** @type {ReturnType<typeof setInterval> | null} */
  #rotateTimer = null;

  /** @type {((event: Event) => void) | null} */
  #onCartUpdate = null;

  /** @type {AbortController | null} */
  #swipeAbort = null;

  /** @type {number | null} */
  #pointerId = null;

  /** @type {number} */
  #pointerStartX = 0;

  /** @type {number} */
  #pointerStartY = 0;

  /** @type {boolean} */
  #isSwipeGesture = false;

  connectedCallback() {
    const configEl = this.querySelector('[data-shinzo-upsell-config]');
    if (configEl?.textContent) {
      try {
        this.#config = JSON.parse(configEl.textContent.trim());
      } catch {
        this.#config = null;
      }
    }

    const headingEl = this.querySelector('.shinzo-cart-upsell__heading');
    if (headingEl && this.#config?.labels?.heading && !headingEl.textContent?.trim()) {
      headingEl.textContent = this.#config.labels.heading;
    }

    this.#onCartUpdate = (event) => {
      const cart =
        event instanceof CustomEvent && event.detail?.resource?.items
          ? event.detail.resource
          : null;

      if (cart) {
        this.#renderFromCart(cart);
        return;
      }

      this.#refreshFromCart();
    };

    document.addEventListener('cart:update', this.#onCartUpdate);
    this.addEventListener('click', this.#onClick);

    const serverSlides = this.querySelectorAll('[data-shinzo-upsell-slide]');
    if (serverSlides.length > 0) {
      this.#slideCount = serverSlides.length;
      this.#activeIndex = 0;
      this.hidden = false;
      this.#initCarouselFromDom();
      return;
    }

    if (this.#config?.items?.length) {
      this.#refreshFromCart();
      return;
    }

    this.hidden = true;
  }

  disconnectedCallback() {
    this.#clearRotation();
    this.#teardownSwipe();
    if (this.#onCartUpdate) {
      document.removeEventListener('cart:update', this.#onCartUpdate);
    }
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const addBtn = target.closest('[data-shinzo-upsell-add]');
    if (addBtn instanceof HTMLButtonElement && this.contains(addBtn)) {
      event.preventDefault();
      const variantId = addBtn.getAttribute('data-variant-id');
      if (variantId) {
        this.#addVariant(variantId, addBtn);
      }
      return;
    }

    const dot = target.closest('[data-shinzo-upsell-dot]');
    if (dot instanceof HTMLButtonElement && this.contains(dot)) {
      event.preventDefault();
      const index = parseInt(dot.getAttribute('data-index') || '', 10);
      if (!Number.isNaN(index)) {
        this.#goTo(index);
        this.#startRotation();
      }
    }
  };

  #initCarouselFromDom() {
    this.#applyTransform();
    this.#setupSwipe();
    this.#startRotation();
  }

  #teardownSwipe() {
    this.#swipeAbort?.abort();
    this.#swipeAbort = null;
    this.#pointerId = null;
    this.#isSwipeGesture = false;

    const viewport = this.querySelector('[data-shinzo-upsell-viewport]');
    if (viewport instanceof HTMLElement) {
      viewport.classList.remove('is-dragging');
    }
  }

  #setupSwipe() {
    this.#teardownSwipe();

    if (this.#slideCount < 2) {
      return;
    }

    const viewport = this.querySelector('[data-shinzo-upsell-viewport]');
    if (!(viewport instanceof HTMLElement)) {
      return;
    }

    this.#swipeAbort = new AbortController();
    const { signal } = this.#swipeAbort;

    viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    viewport.addEventListener('pointermove', this.#onPointerMove, { signal, passive: false });
    viewport.addEventListener('pointerup', this.#onPointerUp, { signal });
    viewport.addEventListener('pointercancel', this.#onPointerUp, { signal });
    viewport.addEventListener('lostpointercapture', this.#onPointerUp, { signal });
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (this.#slideCount < 2) {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const target = event.target;
    if (target instanceof Element && target.closest('button, a, input, textarea, select')) {
      return;
    }

    const viewport = this.querySelector('[data-shinzo-upsell-viewport]');
    if (!(viewport instanceof HTMLElement)) {
      return;
    }

    this.#clearRotation();
    this.#pointerId = event.pointerId;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    this.#isSwipeGesture = false;
    viewport.classList.add('is-dragging');

    try {
      viewport.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture may fail in rare cases; swipe still works via document listeners
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerId !== this.#pointerId) {
      return;
    }

    const deltaX = event.clientX - this.#pointerStartX;
    const deltaY = event.clientY - this.#pointerStartY;

    if (!this.#isSwipeGesture) {
      if (Math.abs(deltaX) < SWIPE_ACTIVATE_PX) {
        return;
      }

      if (Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }

      this.#isSwipeGesture = true;
    }

    event.preventDefault();
    this.#applyTransform(deltaX);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) {
      return;
    }

    const viewport = this.querySelector('[data-shinzo-upsell-viewport]');
    const deltaX = event.clientX - this.#pointerStartX;

    this.#pointerId = null;

    if (viewport instanceof HTMLElement) {
      viewport.classList.remove('is-dragging');

      try {
        viewport.releasePointerCapture(event.pointerId);
      } catch {
        // Already released
      }
    }

    if (this.#isSwipeGesture && Math.abs(deltaX) >= SWIPE_COMMIT_PX) {
      if (deltaX < 0) {
        this.#goTo(this.#activeIndex + 1);
      } else {
        this.#goTo(this.#activeIndex - 1);
      }
    } else {
      this.#applyTransform();
    }

    this.#isSwipeGesture = false;
    this.#startRotation();
  };

  async #refreshFromCart() {
    if (!this.#config?.items?.length) {
      const serverSlides = this.querySelectorAll('[data-shinzo-upsell-slide]');
      this.hidden = serverSlides.length === 0;
      return;
    }

    try {
      const cart = await fetchCartJson();
      if (!cart?.items) {
        throw new Error('Cart unavailable');
      }

      this.#renderFromCart(cart);
    } catch {
      const serverSlides = this.querySelectorAll('[data-shinzo-upsell-slide]');
      if (serverSlides.length === 0) {
        this.hidden = true;
      }
    }
  }

  /** @param {{ items?: Array<{ handle?: string, variant_id?: number, sku?: string, url?: string }> }} cart */
  #renderFromCart(cart) {
    if (!this.#config?.items?.length) {
      const serverSlides = this.querySelectorAll('[data-shinzo-upsell-slide]');
      this.hidden = serverSlides.length === 0;
      return;
    }

    const selected = this.#selectUpsells(cart);
    this.#renderSlides(selected);
    this.hidden = selected.length === 0;
  }

  /**
   * @param {{ items?: Array<{ handle?: string, variant_id?: number, sku?: string, url?: string }> }} cart
   * @returns {UpsellItem[]}
   */
  #selectUpsells(cart) {
    const limits = this.#config?.limits ?? {
      maxTotal: 4,
      maxWhisk: 1,
      maxShaker: 1,
      maxFlavor: 2,
      maxSoda: 1,
    };
    const items = this.#config?.items ?? [];
    const handles = new Set();
    const variantIds = new Set();

    for (const line of cart?.items ?? []) {
      const handle = lineHandle(line);
      if (handle) {
        handles.add(handle);
      }
      if (line?.variant_id != null) {
        variantIds.add(String(line.variant_id));
      }
    }

    const hasDiscovery = [...handles].some((handle) => handle.includes('discovery'));
    const hasWhisk =
      handles.has('drink-mixer') ||
      (cart?.items ?? []).some((line) => String(line?.sku || '').toLowerCase() === 'drink-mixer');
    const hasShaker =
      handles.has('matcha-shaker-bottle') ||
      (cart?.items ?? []).some((line) => String(line?.sku || '').toLowerCase().includes('shaker'));

    /** @type {UpsellItem[]} */
    const selected = [];
    let whiskCount = 0;
    let shakerCount = 0;
    let flavorCount = 0;
    let sodaCount = 0;

    for (const item of items) {
      if (selected.length >= limits.maxTotal) break;

      if (variantIds.has(String(item.variantId))) continue;

      if (item.type === 'whisk') {
        if (hasWhisk || whiskCount >= limits.maxWhisk) continue;
      }

      if (item.type === 'shaker') {
        if (hasShaker || shakerCount >= (limits.maxShaker ?? 1)) continue;
      }

      if (item.type === 'flavor') {
        if (hasDiscovery || handles.has(item.handle) || flavorCount >= limits.maxFlavor) continue;
      }

      if (item.type === 'soda') {
        if (handles.has(item.handle) || sodaCount >= limits.maxSoda) continue;
      }

      selected.push(item);

      if (item.type === 'whisk') whiskCount += 1;
      if (item.type === 'shaker') shakerCount += 1;
      if (item.type === 'flavor') flavorCount += 1;
      if (item.type === 'soda') sodaCount += 1;
    }

    return selected;
  }

  /** @param {UpsellItem[]} items */
  #renderSlides(items) {
    const track = this.querySelector('.shinzo-cart-upsell__track');
    const nav = this.querySelector('.shinzo-cart-upsell__nav');
    const dots = this.querySelector('.shinzo-cart-upsell__dots');

    if (!(track instanceof HTMLElement)) return;

    this.#clearRotation();
    this.#teardownSwipe();
    track.innerHTML = '';

    if (dots instanceof HTMLElement) {
      dots.innerHTML = '';
    }

    this.#slideCount = items.length;
    this.#activeIndex = 0;

    if (items.length === 0) {
      track.style.width = '';
      track.style.transform = '';
      if (nav instanceof HTMLElement) nav.hidden = true;
      return;
    }

    const basisPct = 100 / items.length;
    track.style.width = `${items.length * 100}%`;

    const addLabel = this.#config?.labels?.add ?? 'Add';
    const showOption = this.#config?.labels?.showOption ?? 'Show option';

    for (const [index, item] of items.entries()) {
      const card = document.createElement('article');
      card.className = 'shinzo-cart-upsell__card';
      card.setAttribute('data-shinzo-upsell-slide', '');
      card.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      card.style.flex = `0 0 ${basisPct}%`;

      const frame = document.createElement('div');
      frame.className = 'shinzo-cart-upsell__card-frame';

      const surface = document.createElement('div');
      surface.className = 'shinzo-cart-upsell__card-surface';

      const row = document.createElement('div');
      row.className = 'shinzo-cart-upsell__card-row';

      if (item.image) {
        const media = document.createElement('div');
        media.className = 'shinzo-cart-upsell__media';

        const img = document.createElement('img');
        img.className = 'shinzo-cart-upsell__img';
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.src = item.image;

        media.appendChild(img);
        row.appendChild(media);
      }

      const body = document.createElement('div');
      body.className = 'shinzo-cart-upsell__body';

      const title = document.createElement('p');
      title.className = 'shinzo-cart-upsell__title';
      title.textContent = item.title;
      body.appendChild(title);

      if (item.subtitle) {
        const subtitle = document.createElement('p');
        subtitle.className = 'shinzo-cart-upsell__subtitle';
        subtitle.textContent = item.subtitle;
        body.appendChild(subtitle);
      }

      const meta = document.createElement('p');
      meta.className = 'shinzo-cart-upsell__meta';

      const price = document.createElement('span');
      price.className = 'shinzo-cart-upsell__price';
      price.textContent = item.price;
      meta.appendChild(price);
      meta.append(document.createTextNode(' · '));

      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'shinzo-cart-upsell__add';
      addBtn.textContent = addLabel;
      addBtn.setAttribute('data-shinzo-upsell-add', '');
      addBtn.setAttribute('data-variant-id', String(item.variantId));
      meta.appendChild(addBtn);

      body.appendChild(meta);
      row.appendChild(body);
      surface.appendChild(row);
      frame.appendChild(surface);
      card.appendChild(frame);
      track.appendChild(card);
    }

    if (items.length > 1 && dots instanceof HTMLElement && nav instanceof HTMLElement) {
      nav.hidden = false;
      for (let i = 0; i < items.length; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'shinzo-cart-upsell__dot';
        dot.setAttribute('data-shinzo-upsell-dot', '');
        dot.setAttribute('data-index', String(i));
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${showOption} ${i + 1} of ${items.length}`);
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.tabIndex = i === 0 ? 0 : -1;
        dots.appendChild(dot);
      }
    } else if (nav instanceof HTMLElement) {
      nav.hidden = true;
    }

    this.#applyTransform();
    this.#setupSwipe();
    this.#startRotation();
  }

  #goTo(index) {
    if (this.#slideCount < 1) return;

    this.#activeIndex = ((index % this.#slideCount) + this.#slideCount) % this.#slideCount;
    this.#applyTransform();

    const dots = this.querySelectorAll('[data-shinzo-upsell-dot]');
    dots.forEach((dot, i) => {
      if (!(dot instanceof HTMLButtonElement)) return;
      const selected = i === this.#activeIndex;
      dot.setAttribute('aria-selected', selected ? 'true' : 'false');
      dot.tabIndex = selected ? 0 : -1;
    });

    const cards = this.querySelectorAll('[data-shinzo-upsell-slide]');
    cards.forEach((card, i) => {
      card.setAttribute('aria-hidden', i === this.#activeIndex ? 'false' : 'true');
    });
  }

  /**
   * @param {number} [dragOffsetPx]
   */
  #applyTransform(dragOffsetPx = 0) {
    const track = this.querySelector('.shinzo-cart-upsell__track');
    if (!(track instanceof HTMLElement) || this.#slideCount < 1) {
      return;
    }

    const pct = (this.#activeIndex * 100) / this.#slideCount;

    if (dragOffsetPx) {
      track.style.transform = `translateX(calc(-${pct}% + ${dragOffsetPx}px))`;
      return;
    }

    track.style.transform = `translateX(-${pct}%)`;
  }

  #clearRotation() {
    if (this.#rotateTimer) {
      clearInterval(this.#rotateTimer);
      this.#rotateTimer = null;
    }
  }

  #startRotation() {
    this.#clearRotation();
    if (this.#slideCount < 2) return;

    this.#rotateTimer = setInterval(() => {
      this.#goTo(this.#activeIndex + 1);
    }, AUTO_ROTATE_MS);
  }

  /**
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   */
  async #addVariant(variantId, button) {
    button.disabled = true;

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    const payload = {
      items: [{ id: Number(variantId) || variantId, quantity: 1 }],
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
    }

    const configuredAddUrl =
      typeof window.Theme !== 'undefined' && window.Theme?.routes?.cart_add_url
        ? String(window.Theme.routes.cart_add_url)
        : '/cart/add.js';

    const addUrl = configuredAddUrl.endsWith('.js') ? configuredAddUrl : `${configuredAddUrl}.js`;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed with status ${response.status}`);
      }

      /** @type {{ sections?: Record<string, string>, status?: number, message?: string } | null} */
      let result = null;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        result = await response.json();
      }

      if (result?.status && result?.message) {
        throw new Error(result.message);
      }

      const cart = await fetchCartJson();

      if (cart?.items) {
        this.#renderFromCart(cart);
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            cart,
            data: {
              sections: result?.sections,
              source: 'shinzo-cart-drawer-upsells',
              itemCount: cart?.item_count,
            },
          },
        })
      );
    } catch {
      // Cart refresh will reconcile on next open
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('shinzo-cart-drawer-upsells')) {
  customElements.define('shinzo-cart-drawer-upsells', ShinzoCartDrawerUpsells);
}
